
import { Component, namespace, Prop, Vue } from "nuxt-property-decorator";
import Icon from "~/components/atoms/embedding/Icon.vue";
import RouterLink from "~/components/atoms/text/RouterLink.vue";
import OrderExtraInfo from "~/components/molecules/display/OrderExtraInfo.vue";
import { returnFormattedDate, returnPriceFormatDE } from "~/mixins/formatting";
import { ButtonText, PaymentTypes } from "~/operations/messages";
import { DealerData } from "~/types/Checkout";
import { isPaymentType, PaymentType } from "~/types/PaymentType";
import { distinctSortByTemplate } from "~/utils/array-utils";
import DealerPaymentDetails from "./DealerPaymentDetails.vue";

const CheckoutModule = namespace("checkout");

@Component({
  scrollToTop: true,
  components: {
    RouterLink,
    OrderExtraInfo,
    DealerPaymentDetails,
    Icon,
    // load components when details are clicked
    DealerOfferDetailsOverlay: () =>
      import("~/components/organisms/display/DealerOfferDetailsOverlay.vue"),
    PriceInfoModal: () =>
      import("~/components/molecules/display/PriceInfoModal.vue"),
    CertificateModal: () =>
      import("~/components/molecules/display/CertificateModal.vue"),
  },
  methods: {
    returnPriceFormatDE,
    returnFormattedDate,
  },
})
export default class DealerOfferItemMolecule extends Vue {
  @Prop() index: number;
  @Prop() dealerData: DealerData;
  @Prop() selectPelletsOption: string;
  @Prop() isPelletsSite: boolean;

  @Prop({ default: false }) showPaletColumn: boolean;
  @Prop() dealRequirements: {
    amount: string;
    deliveryTimes: string;
    hose: string;
    payment_type: string;
    prod: string;
    short_vehicle: string;
    unloading_points: string;
    zipcode: string;
    type?: string;
  };

  ButtonText = ButtonText;

  showCertificateModal = false;

  showPriceDetails = false;

  showPriceInfoModal = false;

  @CheckoutModule.Mutation("setValidComparsionPrice")
  setValidComparsionPrice;

  get COMPUTED_ENV_CDN_PATH() {
    return process.env.PARTNER_MEDIA_CDN;
  }

  initFallbackImage(event: Event) {
    const target = event.target as HTMLImageElement;
    if (target instanceof HTMLImageElement) {
      // eslint-disable-next-line no-undef
      target.src = require("~/assets/images/placeholders/no-logo.jpg");
    }
  }

  get totalAmount(): any {
    if (this.$route.query.amount) {
      return this.$route.query.amount;
    }
    return "3000";
  }

  get activePaymentType(): PaymentType {
    const paymentType = this.$route.query.payment_type;
    if (!isPaymentType(paymentType) || paymentType === "all") {
      return this.defaultPaymentType;
    } else {
      return paymentType;
    }
  }

  get defaultPaymentType(): PaymentType {
    // check if requestedPaymentType is still in the caclulator response
    const requestedPaymentType = this.dealerData.dealer.requestedPaymentType;
    if (isPaymentType(requestedPaymentType) && requestedPaymentType !== "all") {
      return requestedPaymentType;
    } else {
      return this.dealerData.dealer.defaultPaymentType as PaymentType;
    }
  }

  get activePaymentTypeName(): string {
    return PaymentTypes[this.activePaymentType];
  }

  get activePaymentTypeImgUrl(): string {
    return `${process.env.PARTNER_MEDIA_CDN}/img/payment-types/${this.activePaymentTypeName}.svg`;
  }

  get availablePaymentTypes(): string[] {
    let types = this.dealerData.dealer.otherPaymentTypes;

    if (this.isPelletsSite) {
      types = types.filter((type) => type !== "guaranteedPayment");
    }

    return distinctSortByTemplate(types, Object.keys(PaymentTypes));
  }

  get otherPaymentTypeCount() {
    const count = this.availablePaymentTypes.filter(
      (val) => val !== "guaranteedPayment",
    ).length;

    return this.activePaymentType === "guaranteedPayment" ? count : count - 1;
  }

  get otherPaymentTypesText() {
    if (this.otherPaymentTypeCount > 0) {
      return `+${this.otherPaymentTypeCount} Weitere Zahlart${
        this.otherPaymentTypeCount > 1 ? "en" : ""
      }`;
    } else if (
      this.otherPaymentTypeCount === 0 &&
      !this.showGuaranteedPaymentInfo
    ) {
      return "keine weiteren Zahlarten";
    }

    return "";
  }

  get showGuaranteedPaymentInfo() {
    return (
      this.activePaymentType !== "guaranteedPayment" &&
      this.availablePaymentTypes.includes("guaranteedPayment")
    );
  }

  get bagsPerPallet(): number {
    return this.dealerData.pricing.common.bagsPerUnit || 65;
  }

  get weightPerBag(): number {
    return this.dealerData.pricing.common.unitWeight || 15;
  }

  get weightPerPallet(): number {
    return this.bagsPerPallet * this.weightPerBag;
  }

  get totalPallets(): number {
    return Math.ceil(this.totalAmount / 1000);
  }

  get formattedPalletInfo(): string {
    return `1 Palette mit ${this.weightPerPallet} kg`;
  }

  get formattedBagInfo(): string {
    return `${this.bagsPerPallet} Säcke je ${this.weightPerBag}kg`;
  }

  get requestedPallets(): number {
    return this.$route.query.amount
      ? parseInt(String(this.$route.query.amount)) / 1000
      : 3; // default amount 3000
  }

  get totalWeight(): number {
    const unitWeight =
      this.selectPelletsOption === "pallet" ? this.weightPerPallet : 1000;
    return unitWeight * this.requestedPallets;
  }

  get totalBrutto() {
    return returnPriceFormatDE(
      this.isPelletsSite
        ? this.dealerData?.pricing?.ton?.brutto
        : this.dealerData?.pricing?._100L?.brutto,
    );
  }

  get deliveryTime(): string {
    if (this.dealerData.delivery.type === "twentyFourHours") {
      return "(in 24 Stunden)";
    } else if (this.dealerData.delivery.durationDays === 0) {
      return "(Heute)";
    } else {
      return `(in ${this.dealerData.delivery.durationDays} Tagen)`;
    }
  }

  get hasEnPlusCertification(): boolean {
    return (
      this.dealerData?.dealer?.otherQualitySteps?.includes("enPlus") || false
    );
  }

  get hasDinPlusCertification(): boolean {
    return (
      this.dealerData?.dealer?.otherQualitySteps?.includes("dinPlus") || false
    );
  }
  showDealerCertificate() {
    this.showCertificateModal = true;
  }

  closeDealerCertificationModal() {
    this.showCertificateModal = false;
  }

  get showPelletsPriceInfo(): boolean {
    return this.selectPelletsOption !== "loose" && this.isPelletsSite;
  }

  get hasValidImageSrc(): boolean {
    return !!this.dealerData.dealer.logoPath;
  }

  get imageSrc(): string {
    return this.hasValidImageSrc
      ? `${this.COMPUTED_ENV_CDN_PATH}${this.dealerData.dealer.logoPath}`
      : // eslint-disable-next-line no-undef
        require("~/assets/images/placeholders/no-logo.jpg");
  }

  togglePriceDetails() {
    this.showPriceDetails = !this.showPriceDetails;
    document.body.style.overflow = this.showPriceDetails ? "hidden" : "auto";
  }

  togglePriceInfoModal() {
    this.showPriceInfoModal = !this.showPriceInfoModal;
    document.body.style.overflow = this.showPriceInfoModal ? "hidden" : "auto";
  }

  redirectToCheckout() {
    this.setValidComparsionPrice(true);

    const baseQuery: any = {
      amount: this.dealRequirements.amount,
      businessCustomerId: this.dealerData.dealer.id,
      businessCustomerSlug: this.dealerData.dealer.slug,
      businessCustomerProfileSlug: this.dealerData.dealer.profileSlug,
      deliveryAreaId: this.dealerData.dealer.daId,
      legacyId: String(this.dealerData.dealer.legacyId ?? -1),
      deliveryTimes: this.dealRequirements.deliveryTimes,
      preselecteddeliveryTimes: this.dealRequirements.deliveryTimes,
      hose: this.dealRequirements.hose,
      payment_type: this.activePaymentType,
      prod: this.dealRequirements.prod,
      preselectedProd: this.dealRequirements.prod,
      short_vehicle: this.dealRequirements.short_vehicle,
      unloading_points: this.dealRequirements.unloading_points,
      zipcode: this.dealRequirements.zipcode,
      checkoutStep: "0",
      numberOfRates: "12",
    };

    if (this.isPelletsSite) {
      baseQuery.quality_steps =
        this.$route.query.quality_steps ?? "isoNorm, enPlus, dinPlus";
      baseQuery.dust_protection = "dustprotection-false";
      baseQuery.totalPallets = this.totalPallets;
      baseQuery.weightPerPallet = this.weightPerPallet;
      baseQuery.weightPerBag = this.weightPerBag;
      baseQuery.bagsPerPallet = this.bagsPerPallet;
      baseQuery.totalWeight = this.totalWeight;
    }

    const path = this.isPelletsSite
      ? "/pelletspreise/pellets-checkout"
      : "/bestellung/heizoel-checkout";

    this.$router.push({
      path,
      query: baseQuery,
    });
  }
}
